import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import API from '../../../services/API'
import PermissionCheck from '../../../helpers/PermissionCheck'
import ExposureOutcomeChip from '../components/ExposureOutcomeChip'
import { EXPOSURE_OUTCOME } from '../labels'

const AssessmentsList = () => {
  const navigate = useNavigate()
  const [assessments, setAssessments] = useState([])
  const [loading, setLoading] = useState(true)
  const [outcomeFilter, setOutcomeFilter] = useState('')

  const load = (outcome) => {
    setLoading(true)
    API.get('/registration/event-assessments', { params: outcome ? { outcome } : {} })
      .then((res) => {
        if (res.status === 200) setAssessments(res.data.assessments)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(outcomeFilter)
  }, [outcomeFilter])

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="title1">Event CQC Exposure Assessments</Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Filter by outcome</InputLabel>
            <Select
              label="Filter by outcome"
              value={outcomeFilter}
              onChange={(e) => setOutcomeFilter(e.target.value)}
            >
              <MenuItem value="">All outcomes</MenuItem>
              {Object.entries(EXPOSURE_OUTCOME)
                .filter(([key]) => key !== 'not_assessed')
                .map(([key, cfg]) => (
                  <MenuItem key={key} value={key}>
                    {cfg.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Stack>

        <Alert severity="info" variant="outlined">
          Every assessment here reflects an indicative outcome only - "likely exempt" or "likely
          requires registration" - never a legal determination. Seek CQC or professional advice for
          borderline or high-stakes cases.
        </Alert>

        {loading && <LinearProgress />}
        {!loading && assessments.length === 0 && (
          <Alert severity="warning">No event assessments recorded yet.</Alert>
        )}

        <Stack gap={1}>
          {assessments.map((a) => (
            <Stack
              key={a._id}
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: a.event?._id ? 'pointer' : 'default',
              }}
              onClick={() => a.event?._id && navigate(`/events/${a.event._id}`)}
            >
              <Stack>
                <Typography variant="body2" fontWeight={700}>
                  {a.event?.title || 'Unknown event'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {a.event?.startDate ? moment(a.event.startDate).format('Do MMM YYYY') : ''}
                  {a.event?.eventLocation ? ` - ${a.event.eventLocation}` : ''}
                  {a.assessedAt && ` - assessed ${moment(a.assessedAt).fromNow()}`}
                </Typography>
              </Stack>
              <ExposureOutcomeChip outcome={a.status === 'completed' ? a.outcome : null} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </PermissionCheck>
  )
}

export default AssessmentsList
